import React, { useState, useMemo, useEffect } from 'react';
import { DialogContent, DialogTitle, Grid, ListItemButton, List, ListItemText, Typography } from '@mui/material';
import { ListItemIcon, Button, TextField, FormControlLabel, Switch, Collapse, MenuItem, Divider } from '@mui/material';
import { Checkbox } from '@mui/material';
import { RoundedDialog, StandardDialogActions } from '../../Components';
import { highlightElementError } from '../../Functions';
import { PanoramaRounded, TableRowsRounded, FunctionsRounded, AddCircleOutlineRounded } from '@mui/icons-material';
import { isArray, isEmpty, isNumber } from 'lodash';
import ConditionItem from './ConditionItem';
import SummaryItem from './SummaryItem';
import StateManager from '../../StateManager';
import { v4 } from 'uuid';

const TABS = [
  { id: 'viewOptions', title: 'Table view settings', icon: <PanoramaRounded /> },
  { id: 'rowSettings', title: 'Table rows settings', icon: <TableRowsRounded /> },
  { id: 'tableSummary', title: 'Table summary', icon: <FunctionsRounded /> },
];

const addAnotherPrefix = 'add another';

export default function SettingsDialog({ open, onClose, initial, onChange, allFields, allColumns }) {
  const [tab, setTab] = useState('viewOptions');

  // Table view options
  const [viewStyle, setViewStyle] = useState('table');
  const [addRowButtonText, setAddRowButtonText] = useState('add another row');

  // Table row settings
  const [requireMinRows, setRequireMinRows] = useState(false);
  const [minRowsSelectType, setMinRowsSelectType] = useState('manual');
  const [minRowsValue, setMinRowsValue] = useState(10);
  const [minRowsFieldId, setMinRowsFieldId] = useState('');
  const [minRowsSetColumnId, setMinRowsSetColumnId] = useState('');
  const [minRowsUseType, setMinRowsUseType] = useState('value');
  const [minRowsFieldConditions, setMinRowsFieldConditions] = useState([]);
  const [rowBarColumns, setRowBarColumns] = useState([]);
  const [allowEditPreBuiltRows, setAllowEditPreBuiltRows] = useState(false);
  const [requiredColumns, setRequiredColumns] = useState([]);

  const availableFieldsForMinRows = useMemo(() => {
    if (!isArray(allFields)) return [];

    const result = allFields.filter(
      (item) =>
        item.title &&
        (item.fieldType === 'number' ||
          (item.fieldType === 'dataSet' &&
            isArray(item.dataSetParams?.columns) &&
            item.dataSetParams.columns.some((x) => x.type === 'number'))),
    );

    return result;
  }, [allFields]);

  const selectedMinRowsField = useMemo(() => {
    if (!minRowsFieldId) return null;

    const result = availableFieldsForMinRows.find((x) => x.id === minRowsFieldId);

    return result;
  }, [minRowsFieldId, availableFieldsForMinRows]);

  // Table summary
  const [summaryFields, setSummaryFields] = useState([]);

  const numberColumns = useMemo(() => {
    if (!isArray(allColumns)) return [];

    const result = allColumns.filter(
      (item) =>
        item.title &&
        (item.fieldType === 'number' ||
          (item.fieldType === 'dataSet' &&
            isArray(item.dataSetParams?.columns) &&
            item.dataSetParams.columns.some((x) => x.type === 'number')) ||
          item.fieldType === 'colorCheck' ||
          (item.fieldType === 'calculation' &&
            item.expressionParams?.format !== 'date' &&
            item.expressionParams?.format !== 'time')),
    );

    return result;
  }, [allColumns]);

  useEffect(() => {
    setViewStyle(initial?.viewStyle || 'table');
    setAddRowButtonText(initial?.addRowButtonText || 'add another row');
    setRequireMinRows(initial?.requireMinRows || false);
    setMinRowsSelectType(initial?.minRowsSelectType || 'manual');
    setMinRowsValue(initial?.minRowsValue || 10);
    setMinRowsFieldId(initial?.minRowsFieldId || '');
    setMinRowsSetColumnId(initial?.minRowsSetColumnId || '');
    setMinRowsUseType(initial?.minRowsUseType || 'value');
    setMinRowsFieldConditions(initial?.minRowsFieldConditions || []);
    setSummaryFields(initial?.summaryFields || []);
    setRowBarColumns(initial?.rowBarColumns || []);
    setAllowEditPreBuiltRows(initial?.allowEditPreBuiltRows || false);
    setRequiredColumns(initial?.requiredColumns || []);
  }, [initial]);

  function done() {
    // check there is field for min rows
    if (requireMinRows && minRowsSelectType === 'field' && !minRowsFieldId) {
      setTab('rowSettings');
      StateManager.setErrorAlert('Please provide the field');
      setTimeout(() => highlightElementError('pick-min-rows-field'), 300);
      return;
    }

    // check there is column for data set field
    if (
      requireMinRows &&
      minRowsSelectType === 'field' &&
      minRowsFieldId &&
      selectedMinRowsField?.fieldType === 'dataSet' &&
      !minRowsSetColumnId
    ) {
      setTab('rowSettings');
      StateManager.setErrorAlert('Please provide the data set column');
      setTimeout(() => highlightElementError('pick-min-rows-set-column'), 300);
      return;
    }

    // validate conditions
    if (requireMinRows && minRowsSelectType === 'field' && minRowsUseType === 'logic') {
      if (isEmpty(minRowsFieldConditions)) {
        setTab('rowSettings');
        StateManager.setErrorAlert('Please provide at least one condition');
        setTimeout(() => highlightElementError('conditions-button'), 300);
        return;
      }
      for (const cond of minRowsFieldConditions) {
        if (!isNumber(cond.rowsToRequire) || !cond.operator) {
          setTab('rowSettings');
          StateManager.setErrorAlert('Condition not valid');
          setTimeout(() => highlightElementError(cond._id), 300);
          return;
        }
      }
    }

    // check summary columns are valid
    for (const item of summaryFields) {
      if (!item.columnId || !item.aggregationFunction) {
        setTab('tableSummary');
        StateManager.setErrorAlert('Invalid summary column');
        setTimeout(() => highlightElementError(item._id), 300);
        return;
      }

      const column = numberColumns.find((x) => x.id === item.columnId);
      if (column?.fieldType === 'dataSet' && !item.dataSetColumnId) {
        setTab('tableSummary');
        StateManager.setErrorAlert('Invalid summary column');
        setTimeout(() => highlightElementError(item._id), 300);
        return;
      }
    }

    const result = {
      viewStyle,
      addRowButtonText,
      requireMinRows,
      minRowsSelectType,
      minRowsValue: requireMinRows ? minRowsValue : null,
      minRowsFieldId: minRowsSelectType === 'field' ? minRowsFieldId : null,
      minRowsSetColumnId: minRowsSelectType === 'field' ? minRowsSetColumnId : null,
      minRowsUseType,
      minRowsFieldConditions: minRowsUseType === 'logic' && minRowsSelectType === 'field' ? minRowsFieldConditions : [],
      summaryFields: summaryFields.filter((item) => allColumns.some((x) => x.id === item.columnId)),
      rowBarColumns,
      allowEditPreBuiltRows,
      requiredColumns: requiredColumns.filter((columnId) => allColumns.some((x) => x.id === columnId)),
    };

    onChange(result);
    onClose();
  }

  function saveCondition(data, index) {
    const copy = [...minRowsFieldConditions];
    copy[index] = { ...copy[index], ...data };
    setMinRowsFieldConditions(copy);
  }

  function deleteCondition(id) {
    setMinRowsFieldConditions(minRowsFieldConditions.filter((x) => x._id !== id));
  }

  function addCondition() {
    const defaultCondition = { _id: v4() };
    setMinRowsFieldConditions([...minRowsFieldConditions, defaultCondition]);
  }

  function addSummary() {
    const defaultSummary = { _id: v4() };
    setSummaryFields([...summaryFields, defaultSummary]);
  }

  function editSummaryItem(data, index) {
    const copy = [...summaryFields];
    copy[index] = { ...copy[index], ...data };
    setSummaryFields(copy);
  }

  function deleteSummary(itemId) {
    setSummaryFields(summaryFields.filter((x) => x._id !== itemId));
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>Table settings</DialogTitle>
      <DialogContent>
        <Grid container sx={{ minHeight: '50vh' }}>
          <Grid container item xs={4} sx={{ pr: 1, height: '100%' }}>
            <List sx={{ width: '100%' }}>
              {TABS.map((item) => (
                <ListItemButton
                  selected={tab === item.id}
                  sx={{
                    border: tab === item.id ? (theme) => `1px solid ${theme.palette.primary.main}` : '',
                    borderRadius: 50,
                  }}
                  onClick={() => setTab(item.id)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ variant: 'body1', color: (theme) => theme.palette.text.secondary }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Grid>
          <Grid container item xs={8} sx={{ px: 2, height: '100%', overflow: 'auto' }} alignContent={'flex-start'}>
            {tab === 'viewOptions' && (
              <Grid container>
                <Grid container>
                  <Typography variant="h6">Table view settings</Typography>
                </Grid>
                <Grid container sx={{ mt: 1 }}>
                  <Typography>
                    Would you like user filling the form out to see the section in a table style or as bars to click on,
                    more of a form style? The PDF output will always be a table style?
                  </Typography>
                </Grid>
                <Grid container sx={{ mt: 1 }}>
                  <Button
                    variant={viewStyle === 'form' ? 'contained' : 'outlined'}
                    sx={{ borderRadius: 50, textTransform: 'none' }}
                    onClick={() => setViewStyle('form')}
                  >
                    Form style
                  </Button>
                  <Button
                    variant={viewStyle === 'table' ? 'contained' : 'outlined'}
                    sx={{ ml: 1, borderRadius: 50, textTransform: 'none' }}
                    onClick={() => setViewStyle('table')}
                  >
                    Table style
                  </Button>
                </Grid>

                <Grid container sx={{ mt: 1 }}>
                  <Typography sx={{ fontStyle: 'italic', color: (theme) => theme.palette.text.secondary }}>
                    On mobile devices the default is form style
                  </Typography>
                </Grid>

                {viewStyle === 'form' && isArray(allColumns) && (
                  <>
                    <Grid container sx={{ mt: 1 }}>
                      <Typography>
                        To help the user know what information is in each row of the table, please pick some columns
                        that you would like to show on the bars
                      </Typography>
                    </Grid>

                    <Grid container sx={{ mt: 1 }}>
                      {allColumns
                        .filter((x) => x.title)
                        .map((column) => (
                          <FormControlLabel
                            key={column.id}
                            label={column.title}
                            control={<Checkbox checked={rowBarColumns.includes(column.id)} />}
                            onChange={(e) => {
                              setRowBarColumns(
                                e.target.checked
                                  ? [...rowBarColumns, column.id]
                                  : rowBarColumns.filter((x) => x !== column.id),
                              );
                            }}
                          />
                        ))}
                    </Grid>
                  </>
                )}

                <Grid container sx={{ mt: 4 }}>
                  <Typography variant="h6">Customise the add row button</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
                    Change the add row button to something that will help the user to know what to do. For example -{' '}
                    <b>add another inspection</b>
                  </Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <TextField
                    sx={{ width: 250 }}
                    value={addRowButtonText}
                    onChange={(e) => {
                      // always leave 'add another'
                      const value = String(e.target.value).startsWith(addAnotherPrefix)
                        ? e.target.value
                        : addAnotherPrefix;
                      setAddRowButtonText(value);
                    }}
                    variant="standard"
                    placeholder="Add another row"
                  />
                </Grid>
              </Grid>
            )}

            {tab === 'rowSettings' && (
              <Grid container>
                <Grid container>
                  <Typography variant="h6">Table rows settings</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={allowEditPreBuiltRows} />}
                    label={'Would you like to allow the user to edit the pre-built rows?'}
                    onChange={(e) => setAllowEditPreBuiltRows(e.target.checked)}
                  />
                </Grid>
                <Grid container direction={'column'}>
                  <Typography sx={{ mt: 1, fontStyle: 'italic' }}>Empty cells will be editable either way</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={requireMinRows} />}
                    label={'Would you like to define the minimum number of rows the user needs to fill out?'}
                    onChange={(e) => setRequireMinRows(e.target.checked)}
                  />
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <Grid container component={Collapse} in={requireMinRows}>
                    <Grid container sx={{ mt: 1 }}>
                      <Button
                        variant={minRowsSelectType === 'manual' ? 'contained' : 'outlined'}
                        sx={{ borderRadius: 50, textTransform: 'none' }}
                        onClick={() => setMinRowsSelectType('manual')}
                      >
                        Define manually
                      </Button>
                      <Button
                        variant={minRowsSelectType === 'field' ? 'contained' : 'outlined'}
                        sx={{ ml: 1, borderRadius: 50, textTransform: 'none' }}
                        onClick={() => setMinRowsSelectType('field')}
                      >
                        Pick a field
                      </Button>
                    </Grid>
                    {minRowsSelectType === 'manual' && (
                      <Grid container sx={{ mt: 2 }}>
                        <TextField
                          sx={{ width: 100 }}
                          value={minRowsValue}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value);
                            if (value !== '' && value < 0) return;
                            setMinRowsValue(value);
                          }}
                          variant="standard"
                          type="number"
                          label="Min table rows"
                        />
                      </Grid>
                    )}

                    {minRowsSelectType === 'field' && (
                      <>
                        <Grid container sx={{ mt: 2 }}>
                          {isEmpty(availableFieldsForMinRows) ? (
                            <Typography>No fields available to choose from</Typography>
                          ) : (
                            <TextField
                              label="Pick field"
                              select
                              variant="standard"
                              sx={{ width: 200 }}
                              value={minRowsFieldId}
                              onChange={(e) => setMinRowsFieldId(e.target.value)}
                              id="pick-min-rows-field"
                            >
                              {availableFieldsForMinRows.map((field) => (
                                <MenuItem value={field.id} key={field.id}>
                                  {field.title}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          {selectedMinRowsField?.fieldType === 'dataSet' &&
                            isArray(selectedMinRowsField.dataSetParams?.columns) &&
                            !isEmpty(selectedMinRowsField.dataSetParams.columns) && (
                              <TextField
                                label="Pick column"
                                select
                                variant="standard"
                                sx={{ width: 200, ml: 1 }}
                                value={minRowsSetColumnId}
                                onChange={(e) => setMinRowsSetColumnId(e.target.value)}
                                id="pick-min-rows-set-column"
                              >
                                {selectedMinRowsField.dataSetParams.columns
                                  .filter((x) => x.type === 'number')
                                  .map((column) => (
                                    <MenuItem value={column.id} key={column.id}>
                                      {column.title}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            )}
                        </Grid>

                        {selectedMinRowsField && (
                          <>
                            <Grid container sx={{ mt: 2 }} direction={'column'}>
                              <Typography>Would you like to use the field value or apply logic?</Typography>
                              {selectedMinRowsField?.fieldType === 'dataSet' && (
                                <Typography sx={{ mt: 1, fontStyle: 'italic' }}>
                                  if multiple rows are selected the maximum value will be used
                                </Typography>
                              )}
                            </Grid>

                            <Grid container sx={{ mt: 2 }}>
                              <Button
                                variant={minRowsUseType === 'logic' ? 'contained' : 'outlined'}
                                sx={{ borderRadius: 50, textTransform: 'none' }}
                                onClick={() => setMinRowsUseType('logic')}
                              >
                                Apply logic
                              </Button>
                              <Button
                                variant={minRowsUseType === 'value' ? 'contained' : 'outlined'}
                                sx={{ ml: 1, borderRadius: 50, textTransform: 'none' }}
                                onClick={() => setMinRowsUseType('value')}
                              >
                                Use field value
                              </Button>
                            </Grid>

                            {minRowsUseType === 'logic' && (
                              <>
                                <Grid container sx={{ mt: 2 }}>
                                  {minRowsFieldConditions.map((condition, index) => (
                                    <React.Fragment key={condition._id}>
                                      <ConditionItem
                                        fieldTitle={selectedMinRowsField?.title}
                                        initial={condition}
                                        onChange={(res) => saveCondition(res, index)}
                                        onDelete={() => deleteCondition(condition._id)}
                                      />
                                      {index !== minRowsFieldConditions.length - 1 && (
                                        <Divider sx={{ width: '100%' }} />
                                      )}
                                    </React.Fragment>
                                  ))}
                                </Grid>

                                <Grid container sx={{ mt: 1 }}>
                                  <Button
                                    variant="outlined"
                                    id="conditions-button"
                                    startIcon={<AddCircleOutlineRounded />}
                                    onClick={() => addCondition()}
                                  >
                                    add condition
                                  </Button>
                                </Grid>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>

                {isArray(allColumns) && (
                  <>
                    <Grid container sx={{ mt: 3 }}>
                      <Typography variant="h6">Required columns</Typography>
                    </Grid>

                    <Grid container sx={{ mt: 1 }}>
                      <Typography>What columns should be required for the user to fill in?</Typography>
                    </Grid>

                    <Grid container sx={{ mt: 1 }}>
                      {allColumns
                        .filter((x) => x.title)
                        .map((column) => (
                          <FormControlLabel
                            key={column.id}
                            label={column.title}
                            control={<Checkbox checked={requiredColumns.includes(column.id)} />}
                            onChange={(e) => {
                              setRequiredColumns(
                                e.target.checked
                                  ? [...requiredColumns, column.id]
                                  : requiredColumns.filter((x) => x !== column.id),
                              );
                            }}
                          />
                        ))}
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {tab === 'tableSummary' && (
              <Grid container>
                <Grid container>
                  <Typography variant="h6">Table summary</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <Grid container>
                    <Typography>
                      Click the summary button to pick columns you would like to summarise as a single result. These can
                      then be used to build KPIs
                    </Typography>
                  </Grid>

                  <Grid container sx={{ mt: 1 }}>
                    {summaryFields.map((item, index) => (
                      <React.Fragment key={item._id}>
                        <SummaryItem
                          initial={item}
                          numberColumns={numberColumns}
                          onChange={(res) => editSummaryItem(res, index)}
                          onDelete={() => deleteSummary(item._id)}
                        />
                        {index !== summaryFields.length - 1 && <Divider sx={{ width: '100%' }} />}
                      </React.Fragment>
                    ))}
                  </Grid>

                  <Grid container sx={{ mt: 1 }}>
                    <Button
                      id="summary-button"
                      variant="outlined"
                      startIcon={<AddCircleOutlineRounded />}
                      onClick={() => addSummary()}
                    >
                      add a summary
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={done} />
    </RoundedDialog>
  );
}
